import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronLeft, ChevronRight, Award, Gift, Check,
    AlertCircle, Loader, Sparkles, CheckCircle,
    Target, GraduationCap, Sparkle
} from 'lucide-react';
import './ProfileCompletionModal.css';

// Helper function to format political leaning display (no negative sign)
const getPoliticalLeaningLabel = (value) => {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue === 0) return 'Neutral';
    if (numValue < 0) return `${Math.abs(numValue)} Liberal`;
    return `${numValue} Conservative`;
};

// Helper function to get political leaning description
const getPoliticalLeaningDescription = (value) => {
    const numValue = parseInt(value);
    if (isNaN(numValue)) return 'Neutral / Moderate';
    if (numValue === -5) return 'Very Liberal';
    if (numValue === -4 || numValue === -3) return 'Liberal';
    if (numValue === -2 || numValue === -1) return 'Slightly Liberal';
    if (numValue === 0) return 'Neutral / Moderate';
    if (numValue === 1 || numValue === 2) return 'Slightly Conservative';
    if (numValue === 3 || numValue === 4) return 'Conservative';
    if (numValue === 5) return 'Very Conservative';
    return 'Neutral / Moderate';
};

// Helper function to format risk tolerance display
const getRiskToleranceLabel = (value) => {
    const numValue = parseInt(value);
    if (isNaN(numValue)) return '5 - Moderate Risk';
    if (numValue <= 2) return `${numValue} - Very Low Risk`;
    if (numValue <= 4) return `${numValue} - Low Risk`;
    if (numValue === 5) return `${numValue} - Moderate Risk`;
    if (numValue <= 7) return `${numValue} - Moderate-High Risk`;
    if (numValue <= 9) return `${numValue} - High Risk`;
    return `${numValue} - Very High Risk`;
};

// Legacy helper for backwards compatibility
const getPoliticalLabel = getPoliticalLeaningDescription;
const getRiskLabel = getRiskToleranceLabel;

// Helper function to calculate age from date of birth
const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Adjust if birthday hasn't occurred this year yet
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    
    return age;
};

// Approved countries list for validation
const APPROVED_COUNTRIES = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
    'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain',
    'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
    'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
    'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada',
    'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros',
    'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
    'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica',
    'Dominican Republic', 'East Timor', 'Ecuador', 'Egypt', 'El Salvador',
    'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji',
    'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
    'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti',
    'Honduras', 'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
    'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan',
    'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia',
    'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania',
    'Luxembourg', 'Macau', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
    'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova',
    'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
    'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria',
    'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau',
    'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines',
    'Poland', 'Portugal', 'Puerto Rico', 'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis',
    'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino',
    'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
    'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
    'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan',
    'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
    'Thailand', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey',
    'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates',
    'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu',
    'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

// Helper function to validate country (case-insensitive)
const validateCountry = (country) => {
    if (!country || !country.trim()) {
        return { isValid: false, error: 'Please enter your country.' };
    }
    
    const trimmedCountry = country.trim().toLowerCase();
    const isValid = APPROVED_COUNTRIES.some(
        approved => approved.toLowerCase() === trimmedCountry
    );
    
    if (!isValid) {
        return { isValid: false, error: "That's not a real country" };
    }
    
    return { isValid: true, error: null };
};

// Helper function to validate date of birth
const validateDateOfBirth = (dateOfBirth) => {
    if (!dateOfBirth) {
        return { isValid: false, error: 'Please enter your date of birth.' };
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    const birthDate = new Date(dateOfBirth);
    birthDate.setHours(0, 0, 0, 0);
    
    // Check if date is valid
    if (isNaN(birthDate.getTime())) {
        return { isValid: false, error: 'Please enter a valid date of birth.' };
    }
    
    // Check if date is in the future
    if (birthDate > today) {
        return { isValid: false, error: 'Date of birth cannot be in the future.' };
    }
    
    const age = calculateAge(dateOfBirth);
    
    // Check minimum age (8 years)
    if (age < 8) {
        return { 
            isValid: false, 
            error: 'Please enter a valid date of birth.' 
        };
    }
    
    // Check maximum age (100 years)
    if (age > 100) {
        return { 
            isValid: false, 
            error: 'Please enter a valid date of birth.' 
        };
    }
    
    return { isValid: true, error: null };
};

// Autocomplete Component with validation support and custom dropdown
const AutocompleteInput = ({ value, onChange, options, placeholder, onValidate, error, onClearError }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const wrapperRef = React.useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                // Validate when clicking outside
                if (onValidate && value) {
                    onValidate(value);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [value, onValidate]);

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        onChange(inputValue);
        
        // Clear error when user starts typing
        if (onClearError) {
            onClearError();
        }
        
        // Filter options based on input
        const filtered = options.filter(option =>
            option.toLowerCase().includes(inputValue.toLowerCase())
        );
        setFilteredOptions(filtered);
        setIsOpen(true);
    };

    const handleFocus = () => {
        // Show all options on focus if no value, otherwise show filtered
        if (!value) {
            setFilteredOptions(options);
        }
        setIsOpen(true);
    };

    const handleOptionClick = (option) => {
        onChange(option);
        setIsOpen(false);
        // Clear error when valid option selected
        if (onClearError) {
            onClearError();
        }
    };

    const toggleDropdown = () => {
        if (!isOpen) {
            setFilteredOptions(value ? options.filter(opt => 
                opt.toLowerCase().includes(value.toLowerCase())
            ) : options);
        }
        setIsOpen(!isOpen);
    };

    return (
        <div className="autocomplete-wrapper" ref={wrapperRef}>
            <div className="autocomplete-input-container">
                <input
                    type="text"
                    value={value}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder={placeholder}
                    className={`text-input autocomplete-input ${error ? 'input-error' : ''}`}
                    autoFocus
                />
                <button 
                    type="button" 
                    className="dropdown-toggle-btn"
                    onClick={toggleDropdown}
                    tabIndex={-1}
                >
                    <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                    >
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </button>
            </div>
            {/* Custom dropdown */}
            {isOpen && filteredOptions.length > 0 && (
                <div className="autocomplete-dropdown">
                    {filteredOptions.map(option => (
                        <div
                            key={option}
                            className="autocomplete-option"
                            onClick={() => handleOptionClick(option)}
                        >
                            {option}
                        </div>
                    ))}
                </div>
            )}
            {/* Error message display */}
            {error && (
                <div className="field-error-message">
                    {error}
                </div>
            )}
        </div>
    );
};

const ProfileCompletionPage = ({ userToken, onComplete }) => {
    const navigate = useNavigate();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [formData, setFormData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState('');
    const [countryError, setCountryError] = useState('');
    const [slideDirection, setSlideDirection] = useState('right');
    const [profileStatus, setProfileStatus] = useState(null);

// All questions for complete profile - 4500 TPHILE reward
const allQuestions = [
    // Essential Profile Questions
    {
        id: 'date_of_birth',
        label: 'What is your date of birth?',
        type: 'date',
        required: true,
        category: 'Essential Profile'
    },
    {
        id: 'gender',
        label: 'How do you identify?',
        type: 'select',
        options: ['Male', 'Female', 'Non-binary', 'Prefer not to say'],
        required: true,
        category: 'Essential Profile'
    },
    {
        id: 'country',
        label: 'Which country are you from?',
        type: 'autocomplete',
        placeholder: 'Start typing your country...',
        options: APPROVED_COUNTRIES,
        required: true,
        category: 'Essential Profile'
    },    
    {
        id: 'primary_language',
        label: 'What\'s your primary language?',
        type: 'select',
        options: ['English', 'Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Arabic', 'Other'],
        required: true,
        category: 'Essential Profile'
    },
    {
        id: 'occupation_category',
        label: 'What field(s) do you work in?',
        subtitle: 'Select up to 3',
        type: 'multiselect',
        max: 3,
        options: [
            'Technology', 'Healthcare', 'Education', 'Finance',
            'Arts & Media', 'Science & Research', 'Business',
            'Government', 'Student', 'Retired', 'Other'
        ],
        required: true,
        category: 'Essential Profile'
    },
    {
        id: 'relationship_status',
        label: 'Relationship status',
        type: 'select',
        options: ['Single', 'In a relationship', 'Married', 'Prefer not to say'],
        required: true,
        category: 'Essential Profile'
    },
    // Interests & Expertise Questions
    {
        id: 'areas_of_expertise',
        label: 'What are your areas of expertise?',
        subtitle: 'Select up to 3',
        type: 'multiselect',
        max: 3,
        options: [
            'Technology', 'Politics', 'Economics', 'Sports',
            'Entertainment', 'Science', 'Health', 'Environment',
            'Business', 'Education', 'Arts', 'Social Issues', 'Other'
        ],
        required: true,
        category: 'Interests & Expertise'
    },
    {
        id: 'education_level',
        label: 'What\'s your highest level of education?',
        type: 'select',
        options: [
            'High School', 'Some College', 'Bachelor\'s Degree',
            'Master\'s Degree', 'Doctoral/PhD', 'Professional Degree',
            'Prefer not to say'
        ],
        required: true,
        category: 'Interests & Expertise'
    },
    {
        id: 'primary_interests',
        label: 'What topics interest you most?',
        subtitle: 'Select up to 5',
        type: 'multiselect',
        max: 5,
        options: [
            'Technology & AI', 'Global Politics', 'Climate Change',
            'Economics & Markets', 'Space Exploration', 'Sports',
            'Entertainment', 'Health & Medicine', 'Social Justice',
            'Education', 'Arts & Culture', 'Business Innovation'
        ],
        required: true,
        category: 'Interests & Expertise'
    },
    {
        id: 'referral_source',
        label: 'How did you hear about us?',
        subtitle: 'Select all that apply (max 3)',
        type: 'multiselect',
        max: 3,
        options: [
            'Social Media', 'Friend/Referral', 'Search Engine',
            'News Article', 'Advertisement'
        ],
        required: true,
        category: 'Interests & Expertise'
    },
    // Prediction Insights Questions
    {
        id: 'political_leaning',
        label: 'Political leaning',
        subtitle: 'Drag the slider to indicate your political stance',
        type: 'political_slider',
        min: -5,
        max: 5,
        default: 0,
        required: true,
        note: 'This helps us understand prediction patterns across different viewpoints',
        category: 'Prediction Insights'
    },
    {
        id: 'risk_tolerance',
        label: 'Risk tolerance in predictions',
        subtitle: 'How comfortable are you with taking risks?',
        type: 'risk_slider',
        min: 1,
        max: 10,
        default: 5,
        required: true,
        note: 'This helps calibrate prediction confidence thresholds',
        category: 'Prediction Insights'
    }
    
];

const totalReward = 4500;


const totalQuestions = allQuestions.length;
const currentQuestion = allQuestions[currentQuestionIndex];

// Calculate progress percentage
const progressPercentage = ((currentQuestionIndex + 1) / totalQuestions) * 100;


    // Handle input changes
    const handleInputChange = (questionId, value) => {
        setFormData(prev => ({
            ...prev,
            [questionId]: value
        }));
        setError('');
    };

    const handleMultiSelect = (questionId, option, max) => {
        const current = formData[questionId] || [];
        let updated;

        if (current.includes(option)) {
            updated = current.filter(item => item !== option);
        } else {
            if (current.length >= max) {
                setError(`You can only select up to ${max} options`);
                return;
            }
            updated = [...current, option];
        }

        setFormData(prev => ({
            ...prev,
            [questionId]: updated
        }));
        setError('');
    };

    // Navigation handlers
    const handleNext = async () => {
        // All questions are now required
        // Handle different types of answers - 0 is a valid answer for sliders!
        let hasAnswer;
        let currentValue = formData[currentQuestion.id];
        
        // For sliders: if no value is set, use the default value
        // This handles the case where user doesn't move the slider from default position
        if ((currentQuestion.type === 'political_slider' || currentQuestion.type === 'risk_slider') && 
            currentValue === undefined) {
            currentValue = currentQuestion.default;
            // Store the default value in formData
            setFormData(prev => ({
                ...prev,
                [currentQuestion.id]: currentQuestion.default
            }));
        }
        
        if (currentQuestion.type === 'multiselect') {
            hasAnswer = currentValue?.length > 0;
        } else if (currentQuestion.type === 'political_slider' || currentQuestion.type === 'risk_slider') {
            // For sliders, 0 is a valid value, so check if it's a number
            hasAnswer = currentValue !== undefined && currentValue !== null;
        } else {
            hasAnswer = currentValue !== undefined && currentValue !== null && currentValue !== '';
        }

        if (!hasAnswer) {
            setError('Please answer this required question');
            return;
        }

        // Validate date of birth if current question is date_of_birth
        if (currentQuestion.id === 'date_of_birth') {
            const dobValidation = validateDateOfBirth(currentValue);
            if (!dobValidation.isValid) {
                setError(dobValidation.error);
                return;
            }
        }

        // Validate country if current question is country
        if (currentQuestion.id === 'country') {
            const countryValidation = validateCountry(currentValue);
            if (!countryValidation.isValid) {
                setCountryError(countryValidation.error);
                setError(countryValidation.error);
                return;
            }
            setCountryError(''); // Clear any previous country error
        }

        // Save progress
        await saveProgress();

        // Move to next question
        if (currentQuestionIndex < totalQuestions - 1) {
            setSlideDirection('right');
            setCurrentQuestionIndex(prev => prev + 1);
            setError('');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            handleSubmit();
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setSlideDirection('left');
            setCurrentQuestionIndex(prev => prev - 1);
            setError('');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleSkip = async () => {
        if (!currentQuestion.required) {
            await saveProgress();
            if (currentQuestionIndex < totalQuestions - 1) {
                setSlideDirection('right');
                setCurrentQuestionIndex(prev => prev + 1);
                setError('');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                handleSubmit();
            }
        }
    };

    const saveProgress = async () => {
        try {
            // Filter out empty/undefined values before saving
            const dataToSave = Object.entries(formData).reduce((acc, [key, value]) => {
                // Only include values that are actually filled
                if (value !== undefined && value !== null && value !== '') {
                    // For arrays, only include if not empty
                    if (Array.isArray(value) && value.length === 0) {
                        return acc;
                    }
                    acc[key] = value;
                }
                return acc;
            }, {});
    
            await fetch(
                `${process.env.REACT_APP_API_URL}/user/profile`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${userToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(dataToSave)
                }
            );
        } catch (error) {
            console.error('Error saving progress:', error);
        }
    };
    

    const handleSubmit = async () => {
        if (isSubmitting) return;
        
        setIsSubmitting(true);
        setError('');
    
        try {
            await saveProgress();
    
            const claimRes = await fetch(
                `${process.env.REACT_APP_API_URL}/user/profile/complete`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${userToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
    
            if (!claimRes.ok) {
                const errorData = await claimRes.json();
                throw new Error(errorData.error || 'Failed to complete profile');
            }
    
            const result = await claimRes.json();
            
            setShowSuccess(true);
            
            setTimeout(() => {
                if (onComplete) {
                    onComplete(result.data.rewardAmount);
                }
                navigate('/login');
            }, 2500);
    
        } catch (err) {
            console.error('Profile completion error:', err);
            setError(err.message);
            setIsSubmitting(false);
        }
    };

    // Check profile completion status
    useEffect(() => {
        const checkProfileStatus = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/user/profile/completion-status`,
                    {
                        headers: {
                            'Authorization': `Bearer ${userToken}`
                        }
                    }
                );
                if (response.ok) {
                    const data = await response.json();
                    setProfileStatus(data.data);
                    
                    // If already completed, redirect to dashboard
                    if (data.data?.isRewarded) {
                        setTimeout(() => {
                            navigate('/login');
                        }, 3000); // 3 second delay to show the completion message
                    }
                }
            } catch (error) {
                console.error('Error checking profile status:', error);
            }
        };

        if (userToken) {
            checkProfileStatus();
        }
    }, [userToken, navigate]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleNext();
            } else if (e.key === 'Enter' && e.shiftKey) {
                e.preventDefault();
                handlePrevious();
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [currentQuestionIndex, formData]);

    // If profile already completed, show thank you message
    if (profileStatus?.isRewarded) {
        return (
            <div className="profile-completion-page">
                <div className="already-completed-overlay">
                    <div className="already-completed-content">
                        <div className="completed-icon-wrapper">
                            <CheckCircle size={100} className="completed-icon" />
                        </div>
                        <h1 className="completed-title">Profile Already Completed!</h1>
                        <p className="completed-message">
                            You've already completed your profile and claimed your reward.
                        </p>
                        <div className="reward-display">
                            <Gift size={32} />
                            <div className="reward-info">
                                <div className="reward-earned-label">Total Earned</div>
                                <div className="reward-earned-amount">{profileStatus.availableReward || 4500} TPHILE</div>
                            </div>
                        </div>
                        {/* Completion stats removed */}
                        <p className="redirect-message">Redirecting to dashboard...</p>
                        <Loader className="redirect-loader" size={24} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-completion-page">
            {/* Sidebar */}
            <aside className="completion-sidebar">
    <div className="sidebar-header">
        <h2>Profile Setup</h2>
        <p>Answer all questions to earn 4500 TPHILE</p>
    </div>

    <div className="progress-info">
        <div className="questions-progress">
            <h3>Progress</h3>
            <div className="progress-circle">
                <svg viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8"/>
                    <circle 
                        cx="50" 
                        cy="50" 
                        r="45" 
                        fill="none" 
                        stroke="#d4af37" 
                        strokeWidth="8"
                        strokeDasharray={`${progressPercentage * 2.827} 283`}
                        strokeLinecap="round"
                        transform="rotate(-90 50 50)"
                    />
                </svg>
                <div className="progress-text">
                    <span className="progress-number">{currentQuestionIndex + 1}</span>
                    <span className="progress-total">/ {totalQuestions}</span>
                </div>
            </div>
            <p className="progress-label">{Math.round(progressPercentage)}% Complete</p>
        </div>
    </div>

    {profileStatus?.isRewarded ? (
        <div className="profile-completed-badge">
            <CheckCircle size={20} />
            <div>
                <div className="completed-title">Profile Completed</div>
                <div className="completed-percentage">100% Complete</div>
            </div>
        </div>
    ) : (
        <div className="total-reward">
            <div className="reward-label">Complete Reward</div>
            <div className="reward-amount">
                <Gift size={20} />
                {totalReward} TPHILE
            </div>
            <p className="reward-note">Answer all {totalQuestions} questions to claim</p>
        </div>
    )}
</aside>


            {/* Main Content */}
            <main className="completion-main">
                {/* Progress Bar */}
                <div className="main-progress-bar">
                    <div 
                        className="main-progress-fill" 
                        style={{ width: `${progressPercentage}%` }}
                    />
                </div>

                {/* Question Counter */}
                <div className="question-meta">
                    <div className="question-counter">
                        Question {currentQuestionIndex + 1} of {totalQuestions}
                    </div>
                    <div className="category-badge">
    {currentQuestion.category}
</div>

                </div>

                {/* Question Content with Slide Animation */}
                <div className={`question-content slide-${slideDirection}`} key={currentQuestionIndex}>
                    <h1 className="question-title">{currentQuestion.label}</h1>
                    {currentQuestion.subtitle && (
                        <p className="question-subtitle">{currentQuestion.subtitle}</p>
                    )}

                    {/* Select Type */}
                    {currentQuestion.type === 'select' && (
                        <div className="options-grid">
                            {currentQuestion.options.map(opt => (
                                <button
                                    key={opt}
                                    className={`option-btn ${formData[currentQuestion.id] === opt ? 'selected' : ''}`}
                                    onClick={() => handleInputChange(currentQuestion.id, opt)}
                                >
                                    {formData[currentQuestion.id] === opt && <Check size={20} />}
                                    <span>{opt}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Text Input */}
                    {currentQuestion.type === 'text' && (
                        <div className="text-input-wrapper">
                            <input
                                type="text"
                                value={formData[currentQuestion.id] || ''}
                                onChange={(e) => handleInputChange(currentQuestion.id, e.target.value)}
                                placeholder={currentQuestion.placeholder}
                                className="text-input"
                                autoFocus
                            />
                        </div>
                        
                    )}

                    {/* Date of Birth Input */}
                    {currentQuestion.type === 'date' && (
                        <div className="date-input-wrapper">
                            <input
                                type="date"
                                value={formData[currentQuestion.id] || ''}
                                onChange={(e) => handleInputChange(currentQuestion.id, e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                                min="1900-01-01"
                                className="date-input"
                                autoFocus
                            />
                            {formData[currentQuestion.id] && (
                                <div className="calculated-age">
                                    <span className="age-label">Your age:</span>
                                    <span className="age-value">{calculateAge(formData[currentQuestion.id])} years old</span>
                                </div>
                            )}
                            {currentQuestion.note && (
                                <p className="question-note">{currentQuestion.note}</p>
                            )}
                        </div>
                    )}

                    {/* Political Leaning Slider */}
                    {currentQuestion.type === 'political_slider' && (
                        <div className="slider-container">
                            <div className="slider-header">
                                <span className="slider-value">
                                    {getPoliticalLeaningLabel(formData[currentQuestion.id] !== undefined ? formData[currentQuestion.id] : currentQuestion.default)}
                                </span>
                                <span className="slider-description">
                                    {getPoliticalLeaningDescription(formData[currentQuestion.id] !== undefined ? formData[currentQuestion.id] : currentQuestion.default)}
                                </span>
                            </div>
                            <div className="slider-wrapper">
                                <span className="slider-label-left">Very Liberal</span>
                                <input
                                    type="range"
                                    id="politicalLeaning"
                                    name="political_leaning"
                                    min="-5"
                                    max="5"
                                    step="1"
                                    value={formData[currentQuestion.id] !== undefined ? formData[currentQuestion.id] : currentQuestion.default}
                                    onChange={(e) => handleInputChange(currentQuestion.id, parseInt(e.target.value))}
                                    className="slider political-leaning-slider"
                                />
                                <span className="slider-label-right">Very Conservative</span>
                            </div>
                            <div className="slider-hint">
                                This helps us understand prediction patterns across different viewpoints
                            </div>
                        </div>
                    )}

                    {/* Risk Tolerance Slider */}
                    {currentQuestion.type === 'risk_slider' && (
                        <div className="slider-container">
                            <div className="slider-header">
                                <span className="slider-value">
                                    {getRiskToleranceLabel(formData[currentQuestion.id] !== undefined ? formData[currentQuestion.id] : currentQuestion.default)}
                                </span>
                            </div>
                            <div className="slider-wrapper">
                                <span className="slider-label-left">Not Risky (1)</span>
                                <input
                                    type="range"
                                    id="riskTolerance"
                                    name="risk_tolerance"
                                    min="1"
                                    max="10"
                                    step="1"
                                    value={formData[currentQuestion.id] !== undefined ? formData[currentQuestion.id] : currentQuestion.default}
                                    onChange={(e) => handleInputChange(currentQuestion.id, parseInt(e.target.value))}
                                    className="slider risk-tolerance-slider"
                                />
                                <span className="slider-label-right">Very Risky (10)</span>
                            </div>
                            <div className="slider-hint">
                                This helps calibrate prediction confidence thresholds
                            </div>
                        </div>
                    )}

                    {/* Autocomplete Input */}
{currentQuestion.type === 'autocomplete' && (
    <AutocompleteInput
        value={formData[currentQuestion.id] || ''}
        onChange={(value) => handleInputChange(currentQuestion.id, value)}
        options={currentQuestion.options}
        placeholder={currentQuestion.placeholder}
        onValidate={currentQuestion.id === 'country' ? (value) => {
            const validation = validateCountry(value);
            if (!validation.isValid) {
                setCountryError(validation.error);
            }
        } : null}
        error={currentQuestion.id === 'country' ? countryError : null}
        onClearError={currentQuestion.id === 'country' ? () => setCountryError('') : null}
    />
)}


                    {/* Multiselect */}
                    {currentQuestion.type === 'multiselect' && (
                        <div className="multiselect-grid">
                            {currentQuestion.options.map(opt => {
                                const isSelected = (formData[currentQuestion.id] || []).includes(opt);
                                return (
                                    <button
                                        key={opt}
                                        className={`option-btn ${isSelected ? 'selected' : ''}`}
                                        onClick={() => handleMultiSelect(currentQuestion.id, opt, currentQuestion.max)}
                                    >
                                        {isSelected && <Check size={20} />}
                                        <span>{opt}</span>
                                    </button>
                                );
                            })}
                            <div className="multiselect-counter">
                                {(formData[currentQuestion.id] || []).length} / {currentQuestion.max} selected
                            </div>
                        </div>
                    )}

{/* Slider */}
{currentQuestion.type === 'slider' && (
    <div className="slider-wrapper">
        <input
            type="range"
            min={currentQuestion.min}
            max={currentQuestion.max}
            value={formData[currentQuestion.id] !== undefined ? formData[currentQuestion.id] : currentQuestion.default}
            onChange={(e) => handleInputChange(currentQuestion.id, parseInt(e.target.value))}
            className="slider-input"
        />
        <div className="slider-value">
            {currentQuestion.id === 'political_leaning' 
                ? Math.abs(formData[currentQuestion.id] !== undefined ? formData[currentQuestion.id] : currentQuestion.default)
                : (formData[currentQuestion.id] !== undefined ? formData[currentQuestion.id] : currentQuestion.default)
            }
        </div>
        {currentQuestion.id === 'political_leaning' && (
            <div className="political-label">
                {getPoliticalLabel(formData[currentQuestion.id] !== undefined ? formData[currentQuestion.id] : currentQuestion.default)}
            </div>
        )}
        {currentQuestion.note && (
            <p className="question-note">{currentQuestion.note}</p>
        )}
    </div>
)}

                </div>

                {/* Error Message */}
                {error && (
                    <div className="error-message">
                        <AlertCircle size={20} />
                        {error}
                    </div>
                )}

                {/* Navigation Buttons */}
                <div className="navigation-buttons">
                    <button
                        onClick={handlePrevious}
                        disabled={currentQuestionIndex === 0}
                        className="nav-btn secondary"
                    >
                        <ChevronLeft size={20} />
                        Previous
                    </button>



                    <button
                        onClick={handleNext}
                        disabled={isSubmitting}
                        className="nav-btn primary"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader className="spinner" size={20} />
                                Submitting...
                            </>
                        ) : currentQuestionIndex === totalQuestions - 1 ? (
                            <>
                                Complete & Claim Reward
                                <Gift size={20} />
                            </>
                        ) : (
                            <>
                                Continue
                                <ChevronRight size={20} />
                            </>
                        )}
                    </button>
                </div>

                {/* Keyboard Hint */}
                <div className="keyboard-hint">
                    Press <kbd>Enter</kbd> to continue • <kbd>Shift + Enter</kbd> to go back
                </div>
            </main>

            {/* Success Overlay */}
            {showSuccess && (
                <div className="success-overlay">
                    <div className="success-content">
                        <div className="success-icon-wrapper">
                            <Gift size={80} className="success-gift-icon" />
                            <div className="success-sparkles">
                                <Sparkles className="sparkle sparkle-1" size={24} />
                                <Sparkles className="sparkle sparkle-2" size={20} />
                                <Sparkles className="sparkle sparkle-3" size={18} />
                            </div>
                        </div>
                        <h2 className="success-title">Profile Complete! 🎉</h2>
                        <p className="success-message">Claiming your reward...</p>
                        <Loader className="success-loader" size={32} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileCompletionPage;
